import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useConstant} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {newPalette} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {AiReview, computeAiReviewStatus} from '../../../../../helpers/models/conversationAiHelpers';
import {AnnotationAiTagSuggestionModel} from '../../../../../models/api/annotationModel';
import {ConversationAiStatusesEnum} from '../../../../../models/api/conversationAiMarkModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';

/*
 * Style.
 */

const StyledDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;

/*
 * Component.
 */

interface AiTagSuggestionControlsProps {
  conversation: ConversationModel | undefined;
  aiSuggestion: AnnotationAiTagSuggestionModel;
  onAiReview: (review: AiReview) => void;
}

export const AiTagSuggestionControls: FC<AiTagSuggestionControlsProps> = ({
  conversation,
  aiSuggestion,
  onAiReview,
}) => {
  // We only have one tag per suggestion.
  const resourceId = useConstant(() => aiSuggestion.tagIds[0]);
  const aiStatus = computeAiReviewStatus(conversation, resourceId, aiSuggestion);

  return <StyledDiv>{renderButtons(aiStatus, onAiReview, resourceId)}</StyledDiv>;
};

function renderButtons(
  aiStatus: ConversationAiStatusesEnum | undefined,
  onAiReview: (review: AiReview) => void,
  resourceId: number,
) {
  switch (aiStatus) {
    case ConversationAiStatusesEnum.APPROVED:
      return (
        <Button size={VisualSizesEnum.SMALL} leftIcon="checkmarkCircleSmall" isDisabled>
          <Trans id="ai-tag-suggestion-approved">Added</Trans>
        </Button>
      );

    case ConversationAiStatusesEnum.DISCARDED:
      return (
        <Button size={VisualSizesEnum.SMALL} isDisabled leftIcon="crossCircleSmall">
          <Trans id="ai-tag-suggestion-discarded">Rejected</Trans>
        </Button>
      );

    default:
      return (
        <>
          <Button
            size={VisualSizesEnum.SMALL}
            leftIcon="checkmarkCircleSmall"
            leftIconColor={newPalette.green.lowlight}
            onClick={() =>
              onAiReview({
                status: ConversationAiStatusesEnum.APPROVED,
                resourceId,
              })
            }
          >
            <Trans id="ai-tag-suggestion-approve">Add tag</Trans>
          </Button>

          <Button
            size={VisualSizesEnum.SMALL}
            leftIcon="crossCircleSmall"
            leftIconColor={newPalette.red.lowlight}
            onClick={() =>
              onAiReview({
                status: ConversationAiStatusesEnum.DISCARDED,
                resourceId,
              })
            }
          >
            <Trans id="ai-tag-suggestion-discard">Reject</Trans>
          </Button>
        </>
      );
  }
}
